"use client";

import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  formatMoney,
  parseMoneyToChargeCents,
  displayInputString,
  CURRENCY_META,
  type PaymentMode,
  type SlidingTier,
  type ChargeCurrency,
  type DisplayCurrency,
} from "@/lib/types/payment";

export type AmountChoice = {
  amount_cents: number | null; // null = no choice yet
  tier_label?: string;
};

type Props = {
  mode: PaymentMode;
  price_cents: number | null;
  suggested_cents: number | null;
  min_cents: number | null;
  sliding_tiers: SlidingTier[] | null;
  /** Charge currency the *_cents are stored in. */
  currency: ChargeCurrency;
  /** Currency shown to the buyer (defaults to charge currency). */
  displayCurrency?: DisplayCurrency;
  value: AmountChoice;
  onChange: (next: AmountChoice) => void;
};

export function AmountSelector(props: Props) {
  const { mode } = props;
  const charge = props.currency;
  const display = props.displayCurrency ?? charge;
  if (mode === "free") return null;

  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
        {mode === "fixed"
          ? "Price"
          : mode === "donation"
            ? "Choose your donation"
            : mode === "sliding_scale"
              ? "Choose a tier"
              : "Pay what you can"}
      </p>

      {mode === "fixed" && (
        <FixedAmount price_cents={props.price_cents} charge={charge} display={display} />
      )}
      {(mode === "donation" || mode === "pwyc") && (
        <FreeAmount
          mode={mode}
          suggested_cents={props.suggested_cents}
          min_cents={props.min_cents}
          charge={charge}
          display={display}
          value={props.value}
          onChange={props.onChange}
        />
      )}
      {mode === "sliding_scale" && (
        <SlidingAmount
          tiers={props.sliding_tiers ?? []}
          charge={charge}
          display={display}
          value={props.value}
          onChange={props.onChange}
        />
      )}
    </div>
  );
}

function FixedAmount({
  price_cents,
  charge,
  display,
}: {
  price_cents: number | null;
  charge: ChargeCurrency;
  display: DisplayCurrency;
}) {
  if (!price_cents) {
    return (
      <p className="text-sm text-destructive">
        This offering hasn&apos;t set a price yet.
      </p>
    );
  }
  return (
    <p className="font-serif text-3xl tracking-tight">
      {formatMoney(price_cents, charge, display)}
    </p>
  );
}

function FreeAmount({
  mode,
  suggested_cents,
  min_cents,
  charge,
  display,
  value,
  onChange,
}: {
  mode: "donation" | "pwyc";
  suggested_cents: number | null;
  min_cents: number | null;
  charge: ChargeCurrency;
  display: DisplayCurrency;
  value: AmountChoice;
  onChange: (next: AmountChoice) => void;
}) {
  // Initialise the input with the suggestion on first render only. The input
  // is in the display currency; amount_cents flowing to the parent is in
  // charge cents.
  const [raw, setRaw] = useState(() => {
    if (value.amount_cents != null)
      return displayInputString(value.amount_cents, charge, display);
    if (mode === "donation" && suggested_cents != null)
      return displayInputString(suggested_cents, charge, display);
    return "";
  });

  // Seed the parent's amount with the donation suggestion on mount. This must
  // happen in an effect, not the useState initialiser above — calling the
  // parent's setState during this child's render is a React error
  // ("Cannot update a component while rendering a different component").
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (
      value.amount_cents == null &&
      mode === "donation" &&
      suggested_cents != null
    ) {
      onChange({ amount_cents: suggested_cents });
    }
    // Mount-only seed; deliberately not reacting to later prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const symbol = currencySymbol(display);
  const zeroLabel = formatMoney(0, charge, display);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="font-serif text-2xl">{symbol}</span>
        <Input
          inputMode="decimal"
          className="h-12 text-lg"
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            const cents = parseMoneyToChargeCents(e.target.value, charge, display);
            onChange({ amount_cents: cents });
          }}
          placeholder={
            mode === "donation" && suggested_cents
              ? displayInputString(suggested_cents, charge, display)
              : "0"
          }
        />
      </div>
      {min_cents != null && min_cents > 0 && (
        <p className="text-xs text-muted-foreground">
          Minimum {formatMoney(min_cents, charge, display)}
        </p>
      )}
      {mode === "donation" && (
        <p className="text-xs text-muted-foreground">
          {suggested_cents
            ? `Donations are optional. Suggested: ${formatMoney(suggested_cents, charge, display)}. Leave at ${zeroLabel} to register without contributing.`
            : `Donations are optional. Leave at ${zeroLabel} to register without contributing.`}
        </p>
      )}
    </div>
  );
}

/** Bare currency symbol (e.g. "R", "€", "₦") for the input prefix. */
function currencySymbol(currency: DisplayCurrency): string {
  const meta = CURRENCY_META[currency];
  const parts = new Intl.NumberFormat(meta.locale, {
    style: "currency",
    currency: meta.code,
  }).formatToParts(0);
  return parts.find((p) => p.type === "currency")?.value ?? meta.code;
}

function SlidingAmount({
  tiers,
  charge,
  display,
  value,
  onChange,
}: {
  tiers: SlidingTier[];
  charge: ChargeCurrency;
  display: DisplayCurrency;
  value: AmountChoice;
  onChange: (next: AmountChoice) => void;
}) {
  if (tiers.length === 0) {
    return (
      <p className="text-sm text-destructive">
        This offering hasn&apos;t set up sliding-scale tiers yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {tiers.map((t) => {
        const selected = value.amount_cents === t.amount_cents;
        return (
          <button
            key={`${t.label}_${t.amount_cents}`}
            type="button"
            onClick={() =>
              onChange({
                amount_cents: t.amount_cents,
                tier_label: t.label,
              })
            }
            className={`flex w-full items-center justify-between rounded-lg border bg-background px-4 py-3 text-left transition-colors ${
              selected
                ? "border-foreground/40 bg-card"
                : "hover:border-foreground/20"
            }`}
          >
            <div>
              <p className="font-serif text-lg">{t.label}</p>
            </div>
            <p
              className={`font-serif text-lg ${
                selected ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {t.amount_cents === 0
                ? "Free"
                : formatMoney(t.amount_cents, charge, display)}
            </p>
          </button>
        );
      })}
    </div>
  );
}

// Keep label for the email/registration record
export type { Label as _Label };
type Label = string;
